import { useTranslationStore } from '@src/stores-v2/StoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useEffect, useLayoutEffect, useRef } from 'react';

const logger = LogManager.getLogger('hook:useMissingTranslationCheck');

/**
 * PLAT-11898 — confirms in the background that the story still has a
 * translation in `language`, and calls `onMissing` if it does not. For a
 * language opened without waiting on that answer (a remembered language, a
 * link's), so a translation deleted elsewhere — another user, another
 * browser — falls back instead of opening on an empty translated view. Runs
 * once per (story, language); a failed listing keeps the language.
 */
export function useMissingTranslationCheck(
  storyId: string | null | undefined,
  language: string | null,
  onMissing: () => void,
): void {
  const translationStore = useTranslationStore();
  // The latest callback, so a re-render doesn't re-run the check.
  const onMissingRef = useRef(onMissing);
  useLayoutEffect(() => {
    onMissingRef.current = onMissing;
  });

  useEffect(() => {
    if (!storyId || !language) return;
    let cancelled = false;
    translationStore
      .listTranslations(storyId)
      .then(languages => {
        if (!cancelled && !languages.includes(language)) {
          onMissingRef.current();
        }
      })
      .catch(error =>
        logger.warn('Could not check the story still has the translation', {
          storyId,
          language,
          ...extractErrorInfo(error),
        }),
      );
    return () => {
      cancelled = true;
    };
  }, [storyId, language, translationStore]);
}
